import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import toast from 'react-hot-toast';
import {
  getFormTemplates, updateFormTemplate, getFormTemplatePdfUrl,
} from '../utils/api';
import styles from './TemplateEditor.module.css';

// Visual PDF form designer for an existing 'pdf' template. Renders each
// page of the template PDF, lets admin/PM:
//   - Pick a tool from the left rail (Text / Number / Date / Checkbox /
//     Dropdown / Signature) and click on a page to drop a new field at
//     the click location with a sensible default size.
//   - Select a field by clicking it; the right rail shows its properties
//     (name, label, type, required toggle, options for dropdown). Delete
//     button removes the field.
//   - Drag the field's center to move it. Drag the bottom-right handle
//     to resize. Snaps to whole pixels.
//   - Save Schema → PATCH /api/form-templates/:id with the new schema.
//
// Existing AcroForm-parsed fields are editable too (label/required/move/
// delete). Their `source` is preserved so the filled-PDF endpoint keeps
// using the AcroForm fill path for them.

pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.js',
  import.meta.url,
).toString();

const RENDER_SCALE = 1.5;

// Per-type default size in PDF points. Used when a tool drops a new field.
const DEFAULTS = {
  text:      { w: 200, h: 22, label: 'Text' },
  number:    { w: 100, h: 22, label: 'Number' },
  date:      { w: 120, h: 22, label: 'Date' },
  checkbox:  { w: 18,  h: 18, label: 'Check' },
  dropdown:  { w: 160, h: 22, label: 'Choose' },
  signature: { w: 240, h: 70, label: 'Signature' },
};

const TYPE_LABEL = {
  text: 'Text', number: 'Number', date: 'Date',
  checkbox: 'Checkbox', radio: 'Radio', dropdown: 'Dropdown',
  signature: 'Signature',
};

// Caller passes the full `template` row (already loaded — typically from
// TemplateManagerModal's list). Avoids the need for a single-template GET.
export default function TemplateEditor({ template: tplProp, onClose }) {
  const [template] = useState(tplProp || null);
  const [fields, setFields]     = useState(tplProp?.schema?.fields || []);
  const [tool, setTool]         = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  const pageRefs = useRef({});
  const dirtyRef = useRef(false);

  // Render PDF pages once template is known.
  useEffect(() => {
    if (!template) return;
    let cancelled = false;
    (async () => {
      try {
        const url = getFormTemplatePdfUrl(template.id);
        const doc = await pdfjsLib.getDocument(url).promise;
        if (cancelled) return;
        for (let i = 1; i <= doc.numPages; i++) {
          if (cancelled) return;
          const page = await doc.getPage(i);
          const viewport = page.getViewport({ scale: RENDER_SCALE });
          const canvas = pageRefs.current[i - 1];
          if (!canvas) continue;
          canvas.width  = viewport.width;
          canvas.height = viewport.height;
          canvas.style.width  = `${viewport.width}px`;
          canvas.style.height = `${viewport.height}px`;
          await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        }
        if (!cancelled) setPdfReady(true);
      } catch (err) {
        if (!cancelled) toast.error(`PDF render failed: ${err.message}`);
      }
    })();
    return () => { cancelled = true; };
  }, [template?.id]);

  function setFieldsDirty(updater) {
    setFields((cur) => {
      const next = typeof updater === 'function' ? updater(cur) : updater;
      dirtyRef.current = true;
      return next;
    });
  }

  // Convert click coords (within the page box) into PDF rect for a new field.
  function makeField(pageIdx, clickX, clickY, pageH, type) {
    const def = DEFAULTS[type];
    const id = `overlay_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    // Generate unique field name.
    const name = uniqueFieldName(type, fields);
    return {
      id,
      name,
      label: def.label,
      type,
      page: pageIdx,
      // Click coord is in scaled CSS pixels relative to page top-left.
      // Convert to PDF user-space (origin bottom-left, points).
      rect: [
        clickX / RENDER_SCALE,
        pageH - (clickY / RENDER_SCALE) - def.h, // flip Y
        def.w, def.h,
      ],
      source: 'overlay',
      required: false,
      ...(type === 'dropdown' ? { options: ['Option 1', 'Option 2'] } : {}),
    };
  }

  function handlePageClick(e, pageIdx, pageH) {
    if (!tool) return; // select mode — clicks on empty space deselect
    // Click came from page background; convert click coord and add field.
    const box = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    const f = makeField(pageIdx, x, y, pageH, tool);
    setFieldsDirty((cur) => [...cur, f]);
    setSelectedId(f.id);
    setTool(null); // back to select after placing
  }

  function handleFieldClick(e, fieldId) {
    e.stopPropagation();
    setSelectedId(fieldId);
    setTool(null);
  }

  function handleDelete(id) {
    setFieldsDirty((cur) => cur.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  }

  function handleFieldPatch(id, patch) {
    setFieldsDirty((cur) => cur.map((f) => f.id === id ? { ...f, ...patch } : f));
  }

  // ── Save ──
  async function handleSave() {
    if (!template) return;
    setSaving(true);
    try {
      const newSchema = {
        ...(template.schema || {}),
        fields,
      };
      await updateFormTemplate(template.id, { schema: newSchema });
      toast.success('Template saved');
      dirtyRef.current = false;
      onClose?.();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (dirtyRef.current && !window.confirm('Discard changes to this template?')) return;
    onClose?.();
  }

  // beforeunload guard
  useEffect(() => {
    function onBefore(e) {
      if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
    }
    window.addEventListener('beforeunload', onBefore);
    return () => window.removeEventListener('beforeunload', onBefore);
  }, []);

  if (!template) {
    return <div className={styles.empty}>No template loaded.</div>;
  }

  const pageSizes = template.schema?.page_sizes || [];
  const selectedField = fields.find((f) => f.id === selectedId) || null;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button className={styles.backBtn} onClick={handleCancel}>Cancel</button>
        <div className={styles.headerCenter}>
          <span className={styles.headerTitle}>Edit · {template.name}</span>
          <span className={styles.headerSub}>
            {fields.length} field{fields.length === 1 ? '' : 's'}
            {dirtyRef.current && <span className={styles.dirty}> · unsaved</span>}
          </span>
        </div>
        <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save Template'}
        </button>
      </header>

      <div className={styles.workspace}>
        {/* Left rail — tool palette */}
        <aside className={styles.toolbar}>
          <div className={styles.toolbarLabel}>Add Field</div>
          {Object.keys(DEFAULTS).map((t) => (
            <button
              key={t}
              className={`${styles.toolBtn} ${tool === t ? styles.toolActive : ''}`}
              onClick={() => setTool(tool === t ? null : t)}
              title={`Click on the page to drop a ${TYPE_LABEL[t]} field`}
            >
              <FieldIcon type={t} /> {TYPE_LABEL[t]}
            </button>
          ))}
          <div className={styles.toolbarHint}>
            Pick a type, then click on the page where you want it.
            Click an existing field to edit its properties.
          </div>
        </aside>

        {/* Center — PDF pages with overlays */}
        <main
          className={styles.scroll}
          onClick={() => { setSelectedId(null); }}
        >
          {!pdfReady && <div className={styles.empty}>Rendering PDF…</div>}
          <div className={styles.pagesWrap}>
            {pageSizes.map((ps, pageIdx) => (
              <div key={pageIdx} className={styles.pageWrap}>
                <div className={styles.pageBadge}>Page {pageIdx + 1} of {pageSizes.length}</div>
                <div
                  className={`${styles.pageBox} ${tool ? styles.placing : ''}`}
                  style={{
                    width:  ps.w * RENDER_SCALE,
                    height: ps.h * RENDER_SCALE,
                  }}
                  onClick={(e) => { e.stopPropagation(); handlePageClick(e, pageIdx, ps.h); }}
                >
                  <canvas
                    ref={(el) => { if (el) pageRefs.current[pageIdx] = el; }}
                    className={styles.pageCanvas}
                  />
                  <div className={styles.overlay}>
                    {fields.filter((f) => f.page === pageIdx).map((f) => (
                      <FieldBox
                        key={f.id}
                        field={f}
                        pageHeight={ps.h}
                        scale={RENDER_SCALE}
                        selected={selectedId === f.id}
                        onSelect={(e) => handleFieldClick(e, f.id)}
                        onPatch={(patch) => handleFieldPatch(f.id, patch)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>

        {/* Right rail — field properties */}
        <aside className={styles.props}>
          {!selectedField && (
            <div className={styles.propsEmpty}>
              Select a field to edit its properties, or pick a tool from the
              left and click on the page to add one.
            </div>
          )}
          {selectedField && (
            <FieldProperties
              field={selectedField}
              onPatch={(patch) => handleFieldPatch(selectedField.id, patch)}
              onDelete={() => handleDelete(selectedField.id)}
            />
          )}
        </aside>
      </div>
    </div>
  );
}

// ── FieldBox ── selectable / draggable / resizable rectangle on the page.
function FieldBox({ field, pageHeight, scale, selected, onSelect, onPatch }) {
  const ref = useRef(null);
  const dragRef = useRef(null);   // { mode: 'move'|'resize', startX, startY, origRect }

  // PDF rect → CSS box. Y is flipped (PDF origin bottom-left).
  const css = {
    left:   field.rect[0] * scale,
    top:    (pageHeight - field.rect[1] - field.rect[3]) * scale,
    width:  field.rect[2] * scale,
    height: field.rect[3] * scale,
  };

  function onPointerDown(e, mode) {
    e.stopPropagation();
    e.preventDefault();
    onSelect(e);
    ref.current.setPointerCapture(e.pointerId);
    dragRef.current = {
      mode,
      startX: e.clientX,
      startY: e.clientY,
      origRect: [...field.rect],
    };
  }
  function onPointerMove(e) {
    if (!dragRef.current) return;
    const dx = (e.clientX - dragRef.current.startX) / scale;
    const dy = (e.clientY - dragRef.current.startY) / scale;
    const [x, y, w, h] = dragRef.current.origRect;
    if (dragRef.current.mode === 'move') {
      // Translate; remember PDF y-up.
      onPatch({ rect: [x + dx, y - dy, w, h] });
    } else if (dragRef.current.mode === 'resize') {
      // Bottom-right handle drags width + height. Y origin shifts up to keep
      // the top-left corner anchored from the user's POV.
      const newW = Math.max(8, w + dx);
      const newH = Math.max(8, h + dy);
      const newY = y - (newH - h); // shift so visual top-left stays put
      onPatch({ rect: [x, newY, newW, newH] });
    }
  }
  function onPointerUp(e) {
    if (!dragRef.current) return;
    dragRef.current = null;
    try { ref.current.releasePointerCapture(e.pointerId); } catch (_) {}
  }

  return (
    <div
      ref={ref}
      className={`${styles.fieldBox} ${selected ? styles.fieldSelected : ''} ${styles[`type_${field.type}`]}`}
      style={css}
      onPointerDown={(e) => onPointerDown(e, 'move')}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      title={`${field.label} (${field.type})`}
    >
      <span className={styles.fieldTag}>
        {field.label || field.name}
        {field.required && <span className={styles.requiredDot}>*</span>}
      </span>
      {selected && (
        <div
          className={styles.resizeHandle}
          onPointerDown={(e) => onPointerDown(e, 'resize')}
        />
      )}
    </div>
  );
}

// ── FieldProperties ── right-rail editor for a single field.
function FieldProperties({ field, onPatch, onDelete }) {
  return (
    <div className={styles.propsBody}>
      <div className={styles.propsHeader}>
        <span className={styles.propsTitle}>Field</span>
        <button
          type="button"
          className={styles.deleteBtn}
          onClick={() => {
            if (window.confirm(`Delete field "${field.label || field.name}"?`)) onDelete();
          }}
        >Delete</button>
      </div>

      <Row label="Label">
        <input
          className={styles.input}
          value={field.label || ''}
          onChange={(e) => onPatch({ label: e.target.value })}
        />
      </Row>
      <Row label="Field name">
        <input
          className={styles.input}
          value={field.name}
          onChange={(e) => onPatch({ name: e.target.value })}
        />
      </Row>
      <Row label="Type">
        {/* Type changes only allowed for overlay fields — AcroForm widgets
            have their type baked into the PDF and we can't safely retype. */}
        {field.source === 'overlay' ? (
          <select
            className={styles.input}
            value={field.type}
            onChange={(e) => onPatch({ type: e.target.value })}
          >
            {Object.keys(DEFAULTS).map((t) => (
              <option key={t} value={t}>{TYPE_LABEL[t]}</option>
            ))}
          </select>
        ) : (
          <span className={styles.inputDisabled}>{TYPE_LABEL[field.type]} (AcroForm)</span>
        )}
      </Row>

      <Row label="Required">
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={!!field.required}
            onChange={(e) => onPatch({ required: e.target.checked })}
          />
          <span>{field.required ? 'Yes — must be filled to submit' : 'Optional'}</span>
        </label>
      </Row>

      {field.type === 'dropdown' && (
        <Row label="Options (one per line)">
          <textarea
            className={styles.textarea}
            rows={5}
            value={(field.options || []).join('\n')}
            onChange={(e) => onPatch({ options: e.target.value.split('\n').map((s) => s.trim()).filter(Boolean) })}
          />
        </Row>
      )}

      <div className={styles.propsMeta}>
        <span className={styles.propsMetaLabel}>Source</span>
        <span className={styles.propsMetaValue}>{field.source || 'acroform'}</span>
      </div>
      <div className={styles.propsMeta}>
        <span className={styles.propsMetaLabel}>Page</span>
        <span className={styles.propsMetaValue}>{field.page + 1}</span>
      </div>
      <div className={styles.propsMeta}>
        <span className={styles.propsMetaLabel}>Position (pts)</span>
        <span className={styles.propsMetaValue}>
          {field.rect.map((n) => Math.round(n)).join(', ')}
        </span>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <label className={styles.rowLabel}>{label}</label>
      {children}
    </div>
  );
}

// Generate a unique-within-template field name for a new field.
function uniqueFieldName(type, fields) {
  const base = type;
  const used = new Set(fields.map((f) => f.name));
  let n = 1;
  while (used.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

// ── Icons (small, by field type) ──
function FieldIcon({ type }) {
  switch (type) {
    case 'text':
      return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M3 4h10M3 8h7M3 12h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>;
    case 'number':
      return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><text x="2" y="12" fontSize="11" fontWeight="700" fill="currentColor">123</text></svg>;
    case 'date':
      return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="2" y="3" width="12" height="11" rx="1" stroke="currentColor" strokeWidth="1.4"/><path d="M2 7h12M5 1v3M11 1v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>;
    case 'checkbox':
      return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="12" height="12" rx="2" stroke="currentColor" strokeWidth="1.4"/><path d="M5 8l2 2 4-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case 'dropdown':
      return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><rect x="2" y="4" width="12" height="8" rx="1" stroke="currentColor" strokeWidth="1.4"/><path d="M5 7l3 3 3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>;
    case 'signature':
      return <svg width="13" height="13" viewBox="0 0 16 16" fill="none"><path d="M2 12c2-1 3-3 5-4s3 0 5 1 2 1 2 0M2 14h12" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>;
    default:
      return null;
  }
}
